/**
 * 无痕 Refresh — 独立实现，避免与 request.ts 循环依赖
 * - 并发去重：多请求同时 401 只打一次 /refresh
 * - 结果区分：ok / invalid（清会话）/ network（不清，避免误踢）/ none
 */

import { API_BASE_URL } from '@/config/env'
import { REQUEST_TIMEOUT } from '@/config/constants'
import { BizCode } from '@/config/enums'
import type { LoginRes } from '@/api/types/user'
import { getRefreshToken, setTokenPair } from '@/utils/auth'
import { getDeviceId } from '@/utils/device'

interface BizResponse<T> {
  code: number
  status: string
  message: string
  data: T
}

/** ok=续期成功；invalid=凭证作废；network=网络/非鉴权失败；none=无 refresh */
export type SilentRefreshResult = 'ok' | 'invalid' | 'network' | 'none'

let refreshing: Promise<SilentRefreshResult> | null = null

function syncUserStoreAfterRefresh(): void {
  void import('@/store/modules/user').then(({ useUserStore }) => {
    useUserStore().syncFromStorage()
  })
}

function isRefreshAuthFailure(statusCode: number, body: BizResponse<LoginRes> | null): boolean {
  if (statusCode === 401 || statusCode === 403) return true
  if (!body || typeof body !== 'object') return statusCode >= 400 && statusCode < 500
  if (body.code === BizCode.UNAUTHORIZED || body.code === BizCode.FORBIDDEN) return true
  const status = String(body.status || '')
  return status.startsWith('AUTH_REFRESH_') || status.startsWith('AUTH_')
}

export function trySilentRefresh(): Promise<SilentRefreshResult> {
  const rt = getRefreshToken()
  if (!rt) return Promise.resolve('none')

  if (!refreshing) {
    refreshing = new Promise<SilentRefreshResult>((resolve) => {
      uni.request({
        url: `${API_BASE_URL}/api/v1/user/refresh`,
        method: 'POST',
        data: { refreshToken: rt, deviceId: getDeviceId() },
        header: {
          'Content-Type': 'application/json',
          'X-Device-Id': getDeviceId(),
        },
        timeout: REQUEST_TIMEOUT,
        success: (res) => {
          const body = res.data as BizResponse<LoginRes>
          if (res.statusCode >= 200 && res.statusCode < 300 && body?.code === BizCode.SUCCESS && body.data?.token) {
            setTokenPair(body.data.token, body.data.refreshToken)
            syncUserStoreAfterRefresh()
            resolve('ok')
            return
          }
          resolve(isRefreshAuthFailure(res.statusCode, body) ? 'invalid' : 'network')
        },
        fail: () => resolve('network'),
        complete: () => {
          refreshing = null
        },
      })
    })
  }
  return refreshing
}
